import { useShowLgbtElements } from '@/hooks/useShowLgbtElements';
import homeLeft from '@/static/assets/home/home-left.svg';
import homeRight from '@/static/assets/home/home-right.svg';
import { Box, Flex, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  CarAnimationStaticAsset,
  CatAnimationStaticAsset,
  GuyAnimationStaticAsset,
  GuysAnimationStaticAsset,
} from './AnimatedStaticAssets';
import { IMAGE_STICK_TO_RIGHT_BREAKPOINT } from './LottieContainer';

const AnimatedElements = dynamic(() => import('./AnimatedElements').then(mod => mod.AnimatedElements), {
  ssr: false,
});

interface ImageProps {
  side: 'left' | 'right';
}

const Spacer = styled(Box)`
  flex: 0;

  ${MEDIA_QUERIES.tablet`
        flex: 0 0 10px;
    `}

  ${MEDIA_QUERIES.desktop`
      flex: 0 0 444px;
  `}
`;

const BackgroundBox = styled(Box)`
  position: ${({ side }) => side === 'left' && 'relative'};

  ${MEDIA_QUERIES.tabletAndDesktop<{ img: string; side: 'left' | 'right' }>`
    display: block;
    flex: 1;
  `}

  @media(min-width: ${IMAGE_STICK_TO_RIGHT_BREAKPOINT}px) {
    background-position: bottom ${({ side }) => (side === 'left' ? 'left' : 'right')};
    flex: 0 0 625px;
  }
`;

const BoxImage = styled(Image)<ImageProps>`
  display: none;
  pointer-events: none;
  user-select: none;
  ${MEDIA_QUERIES.tabletAndDesktop<{ side: 'left' | 'right' }>`
      display: block;
      position: absolute;
      bottom: 0;
      right: ${({ side }) => (side === 'left' ? 0 : 'auto')};

  `}
  ${MEDIA_QUERIES.mobile`
    &.rightImage {
      display: block;
      width: 461px;
      position: absolute;
      bottom: 0;
      right: 0;
      height: 353px;
    }
  `}
  @media (min-width: 1670px) {
    left: 0;
    &.rightImage {
      right: 0;
      left: auto;
    }
  }
  @media (max-width: 425px) {
    &.rightImage {
      right: auto;
      bottom: 0;
      left: 0;
    }
  }
`;

const BackdropContainer = styled(Flex)`
  pointer-events: none;
  height: 100%;
  position: absolute;
  width: 100vw;

  ${MEDIA_QUERIES.tabletAndDesktop`
    top: 54px;
    background: linear-gradient(to top, ${themedColor('cloud')} 56px, transparent 56px);
  `}

  @media (min-width: ${IMAGE_STICK_TO_RIGHT_BREAKPOINT}px) {
    justify-content: space-between;
  }
`;

export const DefaultHero: React.FC = () => {
  const [animationCompleted, setOnAnimationCompleted] = useState<string[]>([]);
  const { showLgbtElements } = useShowLgbtElements();
  return (
    <>
      <BackdropContainer>
        <BackgroundBox side="left">
          <BoxImage src={homeLeft.src} alt="Lemonade House" width={615} height={471} side="left" priority />
          <CatAnimationStaticAsset animationCompleted={animationCompleted} />
          <CarAnimationStaticAsset animationCompleted={animationCompleted} />
          <AnimatedElements
            setOnAnimationCompleted={animation => setOnAnimationCompleted(prev => [...prev, animation])}
          />
        </BackgroundBox>
        <Spacer />
        <BackgroundBox side="right">
          <BoxImage
            src={homeRight.src}
            alt="Lemonade House"
            width={615}
            height={471}
            className="rightImage"
            side="right"
            priority
          />
          {showLgbtElements ? (
            <GuysAnimationStaticAsset animationCompleted={animationCompleted} />
          ) : (
            <GuyAnimationStaticAsset animationCompleted={animationCompleted} />
          )}
          <AnimatedElements
            justGuys
            setOnAnimationCompleted={animation => setOnAnimationCompleted(prev => [...prev, animation])}
          />
        </BackgroundBox>
      </BackdropContainer>
    </>
  );
};
